import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    setDropdownOpen(false);
  };

  const textColor = scrolled ? 'text-foreground-950' : 'text-background-50';
  const textMuted = scrolled ? 'text-foreground-700' : 'text-background-50/90';
  const textHover = scrolled
    ? 'hover:text-foreground-950 hover:bg-foreground-50'
    : 'hover:text-background-50 hover:bg-background-50/10';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-black/15 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Brand */}
          <Link
            to="/"
            onClick={closeAll}
            className="flex items-center gap-2 group"
          >
            <span className={`font-heading text-lg font-semibold whitespace-nowrap ${textColor}`}>
              PandabuySpreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer font-medium ${textMuted} ${textHover}`}
            >
              Home
            </Link>
            <Link
              to="/blog/pandabuy-sheet-ultimate-guide"
              className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer font-medium ${textMuted} ${textHover}`}
            >
              Shopping Guide
            </Link>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-800 bg-background-100 hover:bg-background-200'
                    : 'text-background-50 bg-background-50/15 hover:bg-background-50/25'
                }`}
              >
                <i className="ri-grid-line"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)}></div>
                  <div className="absolute right-0 mt-2 w-72 bg-background-50 rounded-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto shadow-lg">
                    {categories.map((cat) => (
                      <button
                        key={cat.imageUrl}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 border-b border-background-200/70 last:border-b-0 cursor-pointer whitespace-nowrap flex items-center justify-between group"
                      >
                        <span>{cat.name}</span>
                        <i className="ri-external-link-line text-xs text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-100'
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'text-foreground-800 bg-background-100 hover:bg-background-200'
                : 'text-background-50 bg-background-50/15 hover:bg-background-50/25'
            }`}
          >
            <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-line text-base"></i>
              <span className="text-sm font-medium">Home</span>
            </Link>
            <Link
              to="/blog/pandabuy-sheet-ultimate-guide"
              onClick={closeAll}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-base"></i>
              <span className="text-sm font-medium">Shopping Guide</span>
            </Link>
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <i className="ri-grid-line text-base"></i>
                <span className="text-sm font-medium">All Categories</span>
              </div>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileDropdownOpen && (
              <div className="ml-4 border-l-2 border-background-200/70 pl-3 mt-1 space-y-1 max-h-[350px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.imageUrl}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded transition-colors cursor-pointer flex items-center justify-between"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-external-link-line text-xs text-foreground-400"></i>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 px-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="block w-full text-center px-5 py-3 rounded-full text-sm font-medium bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}